import { Button, Card, CardBody, Link } from '@apollo/orbit';
import { Observatory } from '@apollo/space-kit/illustrations/Observatory';
import classnames from 'classnames';
import { GraphQLSchema } from 'graphql';
import React from 'react';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { ChangeLogEntry } from '../../changeLogEntry/ChangeLogEntry';

export const ChangeLogPageLayout = ({
  schemaHistory,
  onRequestMoreChanges,
  hasMore,
  graphqlSchema,
}: {
  schemaHistory: ReadonlyArray<GraphQLTypes.SchemaTagDiffFragment> | undefined;
  onRequestMoreChanges: () => void;
  hasMore: 'maybe' | false;
  graphqlSchema: GraphQLSchema;
}) => {
  const historyItems = schemaHistory?.filter(
    (historyItem) => historyItem.diffToPrevious?.changes.length,
  );
  const { locationToSDL } = useInternalGraphLinking();

  return (
    <div className="overflow-y-auto p-8">
      <Card>
        {!schemaHistory?.length || (!historyItems?.length && !hasMore) ? (
          <CardBody className="items-center pb-24 pt-12 text-center">
            <Observatory className="mb-10" />
            <div className="mb-4 text-3xl font-semibold">
              Let’s get started with the Changelog!
            </div>
            <div className="w-2/3 text-lg leading-relaxed">
              Your <Link to={locationToSDL({})}>published schema</Link> has not
              changed since it was initially registered. Once changes are
              published, return here to see what’s changed. Learn more about{' '}
              <Link
                isExternal
                to="https://www.apollographql.com/docs/graphos/schema/cli-registration"
              >
                schema registration by schema reporting
              </Link>{' '}
              by visiting our docs.
            </div>
          </CardBody>
        ) : (
          <CardBody>
            <ol data-changelog-dates className="list-none pl-0">
              {historyItems?.map((historyItem, i) => (
                <li
                  key={historyItem.id}
                  className={classnames('flex', i > 0 && 'mt-16')}
                >
                  <ChangeLogEntry
                    historyItem={historyItem}
                    graphqlSchema={graphqlSchema}
                  />
                </li>
              ))}
            </ol>
            <Button
              variant="secondary"
              className="mt-8 self-start lg:ml-56"
              onClick={onRequestMoreChanges}
              isDisabled={!hasMore}
            >
              {hasMore ? 'Show more...' : 'No more changes'}
            </Button>
          </CardBody>
        )}
      </Card>
    </div>
  );
};
